/**
 * UserCreditPurchasePage
 * [UC-045] Purchase credits via Stripe
 */
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { api } from '../core/api-client';
import { useTranslation } from 'react-i18next';
import { fmt, fmtMoney } from '../utils/format';

interface CreditPackage {
  creditPackageId: number;
  name: string;
  credits: number;
  price: number;
}

interface UserData {
  userId: number;
  name: string;
  surname: string;
  email: string;
  creditBalance: number;
}

type PurchaseState = 'purchase' | 'success' | 'failed' | 'cancelled';

export function UserCreditPurchasePage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [purchaseState, setPurchaseState] = useState<PurchaseState>(
    (searchParams.get('state') as PurchaseState) || 'purchase'
  );
  const returnToScreen = searchParams.get('returnToScreen');
  const insufficientCreditsMessage = searchParams.get('insufficientCreditsMessage');

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [creditPackages, setCreditPackages] = useState<CreditPackage[]>([]);
  const [selectedPackage, setSelectedPackage] = useState<CreditPackage | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        setError('');

        const [userResponse, packagesResponse] = await Promise.all([
          api.get<{ items: UserData[] }>('/users/me'),
          api.get<{ items: CreditPackage[] }>('/credit-packages')
        ]);

        if (userResponse.items && userResponse.items.length > 0) {
          setUserData(userResponse.items[0]);
        }
        if (packagesResponse.items) {
          setCreditPackages(packagesResponse.items);
        }
      } catch (err: any) {
        console.error('Failed to load data:', err);
        setError('Failed to load data');
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, []);

  const handleCancel = () => {
    if (returnToScreen) {
      navigate(`/${returnToScreen.toLowerCase()}`);
    } else {
      navigate('/user-dashboard');
    }
  };

  const proceedToStripe = async () => {
    if (!selectedPackage || !userData) return;
    try {
      setIsSubmitting(true);
      setError('');
      const successUrl = `${window.location.origin}/user-credit-purchase?state=success${returnToScreen ? `&returnToScreen=${returnToScreen}` : ''}`;
      const cancelUrl = `${window.location.origin}/user-credit-purchase?state=cancelled${returnToScreen ? `&returnToScreen=${returnToScreen}` : ''}`;

      const response = await api.post<{ sessionId: string; checkoutUrl: string }>(
        '/stripe/checkout-sessions',
        { creditPackageId: selectedPackage.creditPackageId, successUrl, cancelUrl }
      );
      window.location.href = response.checkoutUrl;
    } catch (err: any) {
      console.error('Failed to create checkout session:', err);
      setError('Failed to create checkout session. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-4 border-[var(--accent-primary)] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const creditBalance = Number(userData?.creditBalance || 0);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-full overflow-x-hidden">
      {/* Success State */}
      {purchaseState === 'success' && (
        <div className="mb-5 sm:mb-6">
          <div className="bg-green-600/15 border border-green-600/25 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-3">
              <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
              <div>
                <h3 className="text-base font-medium text-green-700">{t('userCreditPurchase.successTitle', 'Credits added')}</h3>
                <p className="text-sm text-green-600 mt-1">{t('userCreditPurchase.successBody', 'Your balance is now {{balance}} credits.', { balance: creditBalance })}</p>
              </div>
            </div>
          </div>
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
            <button onClick={() => navigate('/user-dashboard')} type="button"
              className="w-full sm:w-auto px-5 py-3 sm:py-2.5 bg-[var(--accent-primary)] text-[var(--accent-text)] rounded-lg hover:bg-[var(--accent-secondary)] text-base sm:text-sm font-medium transition-colors">
              {t('userCreditPurchase.returnToDashboard', 'Return to Dashboard')}
            </button>
          </div>
        </div>
      )}

      {/* Payment Failed State */}
      {purchaseState === 'failed' && (
        <div className="mb-5 sm:mb-6">
          <div className="bg-red-600/15 border border-red-600/25 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-3">
              <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
              <div>
                <h3 className="text-base font-medium text-red-700">{t('userCreditPurchase.failedTitle', 'Payment Failed')}</h3>
                <p className="text-sm text-red-600 mt-1">{t('userCreditPurchase.failedBody', 'Your payment could not be processed. Please try again or contact support.')}</p>
              </div>
            </div>
          </div>
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
            <button onClick={() => setPurchaseState('purchase')} type="button"
              className="w-full sm:w-auto px-5 py-3 sm:py-2.5 bg-[var(--surface-raised)] text-[var(--text-primary)] rounded-lg hover:bg-[var(--surface-overlay)] border border-[var(--border-subtle)] text-base sm:text-sm font-medium transition-colors">
              {t('userCreditPurchase.tryAgain', 'Try Again')}
            </button>
            <button onClick={() => navigate('/user-dashboard')} type="button"
              className="w-full sm:w-auto px-5 py-3 sm:py-2.5 bg-[var(--accent-primary)] text-[var(--accent-text)] rounded-lg hover:bg-[var(--accent-secondary)] text-base sm:text-sm font-medium transition-colors">
              {t('userCreditPurchase.contactSupport', 'Contact Support')}
            </button>
          </div>
        </div>
      )}

      {/* Cancelled State */}
      {purchaseState === 'cancelled' && (
        <div className="mb-5 sm:mb-6">
          <div className="bg-amber-600/15 border border-amber-600/25 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-3">
              <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <div>
                <h3 className="text-base font-medium text-amber-700">{t('userCreditPurchase.cancelledTitle', 'Purchase Cancelled')}</h3>
                <p className="text-sm text-amber-600 mt-1">{t('userCreditPurchase.cancelledBody', 'Your purchase was cancelled. No charges were made.')}</p>
              </div>
            </div>
          </div>
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
            <button onClick={() => setPurchaseState('purchase')} type="button"
              className="w-full sm:w-auto px-5 py-3 sm:py-2.5 bg-[var(--surface-raised)] text-[var(--text-primary)] rounded-lg hover:bg-[var(--surface-overlay)] border border-[var(--border-subtle)] text-base sm:text-sm font-medium transition-colors">
              {t('userCreditPurchase.tryAgain', 'Try Again')}
            </button>
            <button onClick={() => navigate('/user-dashboard')} type="button"
              className="w-full sm:w-auto px-5 py-3 sm:py-2.5 bg-[var(--accent-primary)] text-[var(--accent-text)] rounded-lg hover:bg-[var(--accent-secondary)] text-base sm:text-sm font-medium transition-colors">
              {t('userCreditPurchase.returnToDashboard', 'Return to Dashboard')}
            </button>
          </div>
        </div>
      )}

      {/* Purchase State */}
      {purchaseState === 'purchase' && (
        <div>
          <div className="mb-5 sm:mb-6">
            <h1 className="text-2xl lg:text-3xl font-bold text-[var(--text-primary)]">{t('userCreditPurchase.title', 'Purchase credits')}</h1>
            <p className="mt-1.5 text-base sm:text-sm text-[var(--text-secondary)]">{t('userCreditPurchase.subtitle', 'Top up your credit balance to submit change requests and initiate development.')}</p>
            {insufficientCreditsMessage && (
              <div className="mt-3 bg-amber-600/15 border border-amber-600/25 rounded-lg p-3">
                <p className="text-sm text-amber-700">{insufficientCreditsMessage}</p>
              </div>
            )}
          </div>

          {error && (
            <div className="mb-6 bg-red-600/15 border border-red-600/25 rounded-lg p-4">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Current Balance */}
          {userData && (
            <div className="bg-[var(--surface-base)] rounded-lg border border-[var(--border-subtle)] p-4 sm:p-5 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg sm:text-xl font-semibold text-[var(--text-primary)]">{t('userCreditPurchase.currentBalance', 'Current Balance')}</h3>
                  <p className="text-sm text-[var(--text-secondary)] mt-0.5">{t('userCreditPurchase.availableCredits', 'Available in your account')}</p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-[var(--text-primary)]">{fmt(creditBalance)}</div>
                  <div className="text-sm text-[var(--text-muted)]">{t('userCreditPurchase.eurCredits', 'credits')}</div>
                </div>
              </div>
            </div>
          )}

          {/* Credit Packages */}
          <div className="mb-6">
            <h2 className="text-lg sm:text-xl font-semibold text-[var(--text-primary)] mb-4">{t('userCreditPurchase.selectPackage', 'Choose a package')}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {creditPackages.map((pkg) => {
                const isSelected = selectedPackage?.creditPackageId === pkg.creditPackageId;
                return (
                  <div
                    key={pkg.creditPackageId}
                    onClick={() => setSelectedPackage(pkg)}
                    className={`rounded-xl p-5 cursor-pointer transition-all border ${
                      isSelected
                        ? 'border-[var(--accent-primary)] ring-2 ring-[var(--focus-ring)] bg-[var(--accent-soft)]'
                        : 'border-[var(--border-subtle)] bg-[var(--surface-base)] hover:border-[var(--border-default)] hover:bg-[var(--surface-raised)]'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-base font-semibold text-[var(--text-primary)]">{pkg.name}</h3>
                      <div
                        className={`w-5 h-5 rounded-full flex items-center justify-center transition-colors border-2 ${
                          isSelected
                            ? 'border-[var(--accent-primary)] bg-[var(--accent-primary)]'
                            : 'border-[var(--border-default)]'
                        }`}
                      >
                        {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-baseline gap-2">
                        <span className="text-2xl font-bold text-[var(--text-primary)]">{fmtMoney(pkg.price, 2)}</span>
                      </div>
                      <div className="text-sm text-[var(--text-secondary)]">{t('userCreditPurchase.packageCredits', '{{n}} credits', { n: fmt(pkg.price) })}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Purchase Summary */}
          {selectedPackage && userData && (
            <div className="bg-[var(--surface-base)] rounded-lg border border-[var(--border-subtle)] p-4 sm:p-5 mb-6">
              <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-4">{t('userCreditPurchase.summaryTitle', 'Purchase Summary')}</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-base sm:text-sm text-[var(--text-secondary)]">{t('userCreditPurchase.summaryPackage', 'Package')}</span>
                  <span className="text-base sm:text-sm font-medium text-[var(--text-primary)]">{selectedPackage.name}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-base sm:text-sm text-[var(--text-secondary)]">{t('userCreditPurchase.summaryCredits', 'Credits')}</span>
                  <span className="text-base sm:text-sm font-medium text-[var(--text-primary)]">{fmt(selectedPackage.credits)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-base sm:text-sm text-[var(--text-secondary)]">{t('userCreditPurchase.summaryPrice', 'Price')}</span>
                  <span className="text-base sm:text-sm font-medium text-[var(--text-primary)]">{fmtMoney(selectedPackage.price, 2)}</span>
                </div>
                <div className="border-t border-[var(--border-subtle)] pt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-base font-medium text-[var(--text-primary)]">{t('userCreditPurchase.summaryNewBalance', 'New Balance')}</span>
                    <span className="text-base font-bold text-[var(--text-primary)]">{t('userCreditPurchase.packageCredits', '{{n}} credits', { n: fmt(creditBalance + Number(selectedPackage.credits)) })}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
            <button onClick={handleCancel} type="button" disabled={isSubmitting}
              className="w-full sm:w-auto px-5 py-3 sm:py-2.5 bg-[var(--surface-raised)] text-[var(--text-primary)] rounded-lg hover:bg-[var(--surface-overlay)] border border-[var(--border-subtle)] text-base sm:text-sm font-medium transition-colors">
              {t('common.cancel', 'Cancel')}
            </button>
            <button onClick={proceedToStripe} disabled={!selectedPackage || isSubmitting} type="button"
              className={`w-full sm:w-auto px-5 py-3 sm:py-2.5 rounded-lg text-base sm:text-sm font-medium transition-colors ${
                selectedPackage && !isSubmitting
                  ? 'bg-[var(--accent-primary)] text-[var(--accent-text)] hover:bg-[var(--accent-secondary)]'
                  : 'bg-[var(--surface-overlay)] text-[var(--text-muted)] cursor-not-allowed'
              }`}>
              {isSubmitting ? t('userCreditPurchase.processing', 'Processing...') : t('userCreditPurchase.continueToStripe', 'Continue to Stripe Checkout')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserCreditPurchasePage;
